import { Component } from 'react';
import assign from 'lodash/assign';
import difference from 'lodash/difference';
import each from 'lodash/each';
import filter from 'lodash/filter';
import has from 'lodash/has';
import intersection from 'lodash/intersection';
import invariant from 'invariant';
import invoke from 'lodash/invoke';
import isEmpty from 'lodash/isEmpty';
import isUndefined from 'lodash/isUndefined';
import keys from 'lodash/keys';
import reduce from 'lodash/reduce';
import startsWith from 'lodash/startsWith';

const getDefaultPropName = prop =>
  `default${prop[0].toUpperCase() + prop.slice(1)}`;

export const getAutoControlledStateValue = (
  propName,
  props,
  state,
  includeDefaults = false
) => {
  // regular props
  const propValue = props[propName];
  if (propValue !== undefined) return propValue;

  if (includeDefaults) {
    // defaultProps
    const defaultProp = props[getDefaultPropName(propName)];
    if (defaultProp !== undefined) return defaultProp;

    // initial state - state may be null or undefined
    if (state) {
      const initialState = state[propName];
      if (initialState !== undefined) return initialState;
    }
  }

  // React doesn't allow changing from uncontrolled to controlled components,
  // default checked/value if they were not present.
  if (propName === 'checked') return false;
  if (propName === 'value') return props.multiple ? [] : '';

  // otherwise, undefined
};

export default class AutoControlledComponent extends Component {
  constructor(...args) {
    super(...args);

    const { autoControlledProps } = this.constructor;
    const state =
      invoke(this, 'getInitialAutoControlledState', this.props) || {};

    if (process.env.NODE_ENV !== 'production') {
      const { defaultProps, name, propTypes } = this.constructor;

      invariant(
        !isEmpty(autoControlledProps),
        `Auto controlled ${name} must specify a static autoControlledProps Array.`
      );

      each(autoControlledProps, prop => {
        const defaultProp = getDefaultPropName(prop);

        invariant(
          !has(propTypes, defaultProps),
          `${name} is missing "${defaultProp}" propTypes validation for auto controlled prop "${prop}".`
        );

        invariant(
          has(propTypes, prop),
          `${name} is missing propTypes validation for auto controlled prop "${prop}".`
        );
      });

      const illegalDefaults = intersection(
        autoControlledProps,
        keys(defaultProps)
      );
      invariant(
        isEmpty(illegalDefaults),
        [
          'Do not set defaultProps for autoControlledProps. You can set defaults by',
          'setting state in the constructor or using an ES7 property initializer',
          '(https://babeljs.io/blog/2015/06/07/react-on-es6-plus#property-initializers)',
          `See ${name} props: "${illegalDefaults}".`
        ].join(' ')
      );

      const illegalAutoControlled = filter(autoControlledProps, prop =>
        startsWith(prop, 'default')
      );
      invariant(
        isEmpty(illegalAutoControlled),
        [
          'Do not add default props to autoControlledProps.',
          'Default props are automatically handled.',
          `See ${name} autoControlledProps: "${illegalAutoControlled}".`
        ].join(' ')
      );
    }

    const initialAutoControlledState = reduce(
      autoControlledProps,
      (acc, prop) => {
        acc[prop] = getAutoControlledStateValue(prop, this.props, state, true);

        if (process.env.NODE_ENV !== 'production') {
          const defaultPropName = getDefaultPropName(prop);
          const { name } = this.constructor;
          // prevent defaultFoo={} along side foo={}

          if (
            !isUndefined(this.props[defaultPropName]) &&
            !isUndefined(this.props[prop])
          ) {
            console.error(
              `${name} prop "${prop}" is auto controlled. Specify either ${defaultPropName} or ${prop}, but not both.`
            );
          }
        }

        return acc;
      },
      {}
    );

    this.state = { ...state, ...initialAutoControlledState };
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    const { autoControlledProps } = this.constructor;

    const newState = reduce(
      autoControlledProps,
      (acc, prop) => {
        const isNextUndefined = isUndefined(nextProps[prop]);
        const propWasRemoved =
          !isUndefined(this.props[prop]) && isNextUndefined;

        // if next is defined then use its value
        if (!isNextUndefined) acc[prop] = nextProps[prop];
        // reinitialize state for props just removed / set undefined
        else if (propWasRemoved)
          acc[prop] = getAutoControlledStateValue(prop, nextProps);

        return acc;
      },
      {}
    );
    if (keys(newState).length > 0) this.setState(newState);
  }

  trySetState = (maybeState, state) => {
    const { autoControlledProps } = this.constructor;
    if (process.env.NODE_ENV !== 'production') {
      const { name } = this.constructor;

      const illegalKeys = difference(keys(maybeState), autoControlledProps);
      invariant(
        isEmpty(illegalKeys),
        [
          `${name} called trySetState() with controlled props: "${illegalKeys}".`,
          'State will not be set.',
          'Only props in static autoControlledProps will be set on state.'
        ].join(' ')
      );
    }
    let newState = reduce(
      keys(maybeState),
      (acc, prop) => {
        // ignore props defined by the parent
        if (this.props[prop] !== undefined) return acc;

        // ignore props not listed in auto controlled props
        if (autoControlledProps.indexOf(prop) === -1) return acc;

        acc[prop] = maybeState[prop];
        return acc;
      },
      {}
    );

    if (state) newState = assign(newState, state);

    if (keys(newState).length > 0) this.setState(newState);
  };
}
